// src/marketing/launch/hooks/useLaunchPlans.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../lib/api'
import type { LaunchPlan } from '../../../lib/types'

const STALE = 1000 * 60 * 2

const launchPlanKeys = {
  all: ['launch-plans'] as const,
  list: (appId: string) => ['launch-plans', 'list', appId] as const,
  detail: (id: string) => ['launch-plans', 'detail', id] as const,
}

export function useLaunchPlans(appId: string | null) {
  return useQuery({
    queryKey: appId ? launchPlanKeys.list(appId) : launchPlanKeys.all,
    queryFn: () => api.get<{ plans: LaunchPlan[] }>(`/api/launch-plans?appId=${appId}`).then((r) => r.plans),
    enabled: appId !== null,
    staleTime: STALE,
  })
}

export function useLaunchPlan(id: string | null) {
  return useQuery({
    queryKey: id ? launchPlanKeys.detail(id) : launchPlanKeys.all,
    queryFn: () => api.get<{ plan: LaunchPlan }>(`/api/launch-plans/${id}`).then((r) => r.plan),
    enabled: id !== null,
    staleTime: STALE,
  })
}

type CreatePlanInput = {
  appId: string
  name: string
  launchDate: string | null
  duplicateOf?: string | null
}

export function useCreateLaunchPlan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreatePlanInput) =>
      api.post<{ plan: LaunchPlan; tasksInserted: number }>('/api/launch-plans', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: launchPlanKeys.all })
    },
  })
}

type UpdatePlanInput = { name?: string; launchDate?: string | null; status?: string }

export function useUpdateLaunchPlan(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: UpdatePlanInput) =>
      api.put<{ plan: LaunchPlan }>(`/api/launch-plans/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: launchPlanKeys.all })
    },
  })
}

export function useDeleteLaunchPlan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.del<{ success: boolean }>(`/api/launch-plans/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: launchPlanKeys.all })
    },
  })
}
